import { Alert, Flex, Image, List, Spacer } from '@zillow/constellation';
import { FormField, FormHelp, Heading, Input, Label, Text, TextButton } from '@zillow/constellation-10';
import VirtualTour from '../../../../../images/VirtualTour.svg';

import { StyledList } from './StyledComponents';
import SpinnerContainer from '../../../../SpinnerContainer';

import { useGetExternalTourProviders } from '../../../../../hooks/queries/useGetExternalTourProviders';

import AppSelectors from '../../../../../selectors/AppSelectors';
import { useSelector } from '../../../../../selectors/useSelector';

import type { ChangeEvent, MutableRefObject } from 'react';
import type { ExternalTourProps } from '../../../../../model/ExternalTour';

const { makeIsScreenMobile } = AppSelectors;

type Props = {
  externalTour: ExternalTourProps;
  onUpdateExternalTour: (label: string, value: string) => void;
  tourUrlErrorMessage?: string | null;
  externalTourInputRef: MutableRefObject<null> | null;
};

const ExternalTour = ({ externalTour, onUpdateExternalTour, tourUrlErrorMessage, externalTourInputRef }: Props) => {
  const {
    data: externalTourProviders,
    isError,
    isLoading,
    refetch: refetchGetExternalTourProviders,
  } = useGetExternalTourProviders();

  const isScreenMobile = useSelector((state) => makeIsScreenMobile(state));

  const renderProviderCols = () => {
    if (externalTourProviders) {
      const numDisplayCols = isScreenMobile ? 2 : 4;
      const numPerCol = externalTourProviders.length / numDisplayCols;

      let columnStartIdxs = Array(numDisplayCols)
        .fill(0)
        .map((val, idx) => numPerCol * idx);
      if (externalTourProviders.length % numDisplayCols > 0) {
        columnStartIdxs = columnStartIdxs.map((val, idx) => (idx > 0 ? val + 1 : val));
      }

      return columnStartIdxs.map((startIdx, idx) =>
        columnStartIdxs[idx + 1] ? (
          <StyledList>
            {externalTourProviders.slice(startIdx, columnStartIdxs[idx + 1]).map((name) => (
              <List.Item paddingY={0}>{name}</List.Item>
            ))}
          </StyledList>
        ) : (
          <StyledList>
            {externalTourProviders.slice(startIdx).map((name) => (
              <List.Item paddingY={0}>{name}</List.Item>
            ))}
          </StyledList>
        ),
      );
    }
  };

  return (
    <Spacer paddingTop="xs">
      <Flex display="flex" alignItems="center">
        <Image src={VirtualTour} alt="" paddingRight="sm" />
        <Heading level={2} textStyle="heading-sm">
          Provide the link to your tour
        </Heading>
      </Flex>
      <Spacer paddingY="md">
        <Spacer paddingBottom="xs">
          <Text>Add a tour link from one of the supported sources:</Text>
        </Spacer>
        {isLoading && (
          <Flex display="flex" alignItems="center" justifyContent="center">
            <SpinnerContainer height="auto" />
          </Flex>
        )}
        {isError && (
          <Alert
            appearance="error"
            actionButton={<TextButton onClick={() => refetchGetExternalTourProviders()}>Retry now</TextButton>}
            body="We are having issues loading external tour providers."
          />
        )}
        <Flex display="flex" flexDirection="row" flexWrap="wrap" justifyContent="space-between">
          {renderProviderCols()}
        </Flex>
      </Spacer>
      <FormField
        css={{ marginBlock: '300' }}
        required={true}
        label={<Label>Tour name</Label>}
        control={
          <Input
            placeholder="New tour"
            value={externalTour.label}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onUpdateExternalTour('label', e.target.value)}
          />
        }
        description={<FormHelp>Modify to a specific tour name (example: &quot;large 1 bedroom&quot;).</FormHelp>}
      />
      <FormField
        css={{ marginBlock: '300' }}
        required={true}
        label={<Label>Tour link</Label>}
        control={
          <Input
            placeholder="https://app.cloudpano.com/tours/"
            value={externalTour.tourUrl}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onUpdateExternalTour('tourUrl', e.target.value)}
            ref={externalTourInputRef}
            data-testid="SelectTourModal-ExternalTour-tourUrl"
          />
        }
        error={!!tourUrlErrorMessage}
        description={tourUrlErrorMessage ? <FormHelp>{tourUrlErrorMessage}</FormHelp> : null}
      />
    </Spacer>
  );
};

export default ExternalTour;
